import { Box, Tooltip } from '@mui/material'
import StorefrontIcon from '@mui/icons-material/Storefront'
import { NotificationEventType } from 'types/notification'
import { useTranslation } from 'react-i18next'

interface NotificationIconProps {
  type: NotificationEventType
  size: number
  colour: boolean
  orderId?: string
}

export const NotificationIcon = ({
  type,
  size,
  colour,
  orderId,
}: NotificationIconProps) => {
  const { t } = useTranslation()
  const red = colour ? '#D0021B' : 'black'
  const green = colour ? '#4caf50' : 'black'

  const getTooltip = () => {
    if (!orderId) return ''
    return t(`notification_${type}`, { order_id: orderId })
  }

  const getIcon = () => {
    switch (type) {
      case 'order_created':
        return (
          <svg
            width={size}
            height={size}
            viewBox='0 0 24 24'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'>
            <path
              d='M11 9H13V6H16V4H13V1H11V4H8V6H11V9ZM7 18C5.9 18 5.01 18.9 5.01 20C5.01 21.1 5.9 22 7 22C8.1 22 9 21.1 9 20C9 18.9 8.1 18 7 18ZM17 18C15.9 18 15.01 18.9 15.01 20C15.01 21.1 15.9 22 17 22C18.1 22 19 21.1 19 20C19 18.9 18.1 18 17 18ZM8.1 13H15.55C16.3 13 16.96 12.59 17.3 11.97L21.16 4.96L19.41 4L15.55 11H8.53L4.27 2H1V4H3L6.6 11.59L5.25 14.03C4.52 15.37 5.48 17 7 17H19V15H7L8.1 13Z'
              fill={green}
            />
          </svg>
        )
      case 'order_late_to_be_picked':
        return (
          <svg
            width={size}
            height={size}
            viewBox='0 0 24 24'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'>
            <path
              d='M12 9.5C11.7167 9.5 11.4792 9.40417 11.2875 9.2125C11.0958 9.02083 11 8.78333 11 8.5C11 8.21667 11.0958 7.97917 11.2875 7.7875C11.4792 7.59583 11.7167 7.5 12 7.5C12.2833 7.5 12.5208 7.59583 12.7125 7.7875C12.9042 7.97917 13 8.21667 13 8.5C13 8.78333 12.9042 9.02083 12.7125 9.2125C12.5208 9.40417 12.2833 9.5 12 9.5ZM11 6V1H13V6H11ZM7 22C6.45 22 5.97917 21.8042 5.5875 21.4125C5.19583 21.0208 5 20.55 5 20C5 19.45 5.19583 18.9792 5.5875 18.5875C5.97917 18.1958 6.45 18 7 18C7.55 18 8.02083 18.1958 8.4125 18.5875C8.80417 18.9792 9 19.45 9 20C9 20.55 8.80417 21.0208 8.4125 21.4125C8.02083 21.8042 7.55 22 7 22ZM17 22C16.45 22 15.9792 21.8042 15.5875 21.4125C15.1958 21.0208 15 20.55 15 20C15 19.45 15.1958 18.9792 15.5875 18.5875C15.9792 18.1958 16.45 18 17 18C17.55 18 18.0208 18.1958 18.4125 18.5875C18.8042 18.9792 19 19.45 19 20C19 20.55 18.8042 21.0208 18.4125 21.4125C18.0208 21.8042 17.55 22 17 22ZM1 4V2H4.275L8.525 11H15.525L19.425 4H21.7L17.3 11.95C17.1167 12.2833 16.8708 12.5417 16.5625 12.725C16.2542 12.9083 15.9167 13 15.55 13H8.1L7 15H19V17H7C6.25 17 5.67917 16.675 5.2875 16.025C4.89583 15.375 4.88333 14.7167 5.25 14.05L6.6 11.6L3 4H1Z'
              fill={red}
            />
          </svg>
        )
      case 'order_late_to_be_dispatched':
        return (
          <svg
            width={size}
            height={size}
            viewBox='0 0 24 24'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'>
            <path
              fillRule='evenodd'
              clipRule='evenodd'
              d='M16.5675 3C17.424 3 18.1854 3.54539 18.461 4.35633L20.0384 8.99672L22.2577 10.7916C22.7272 11.1713 23 11.7428 23 12.3467V18H20.5C20.5 19.6569 19.1569 21 17.5 21C15.8431 21 14.5 19.6569 14.5 18H11C11 19.6569 9.65685 21 8 21C6.34315 21 5 19.6569 5 18H4C3.44772 18 3 17.5523 3 17V16L5.76381 16.0001C6.31313 15.3863 7.11145 15 8 15C8.88855 15 9.68687 15.3863 10.2362 16.0001H15.2638C15.8131 15.3863 16.6115 15 17.5 15C18.3885 15 19.1869 15.3863 19.7362 16.0001L21 16V12L18.6123 10.41L16.6187 5H13.5239C13.1255 5 12.7651 4.76351 12.6066 4.39803L12 3H16.5675ZM17.5 16.5C16.6716 16.5 16 17.1716 16 18C16 18.8284 16.6716 19.5 17.5 19.5C18.3284 19.5 19 18.8284 19 18C19 17.1716 18.3284 16.5 17.5 16.5ZM8 16.5C7.17157 16.5 6.5 17.1716 6.5 18C6.5 18.8284 7.17157 19.5 8 19.5C8.82843 19.5 9.5 18.8284 9.5 18C9.5 17.1716 8.82843 16.5 8 16.5Z'
              fill={red}
            />
            <path
              d='M8 1C11.864 1 15 4.136 15 8C15 11.864 11.864 15 8 15C4.136 15 1 11.864 1 8C1 4.136 4.136 1 8 1ZM8 3C5.24 3 3 5.24 3 8C3 10.76 5.24 13 8 13C10.76 13 13 10.76 13 8C13 5.24 10.76 3 8 3ZM8.8 4.5H7.3V8.5899H8.8V4.5Z'
              fill={red}
            />
            <path d='M8.79999 10H7.29999V11.4H8.79999V10Z' fill={red} />
          </svg>
        )
      case 'order_late_to_be_delivered':
        return (
          <svg
            width={size}
            height={size}
            viewBox='0 0 24 24'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'>
            <path
              fillRule='evenodd'
              clipRule='evenodd'
              d='M16.5675 3C17.424 3 18.1854 3.54539 18.461 4.35633L20.0384 8.99672L22.2577 10.7916C22.7272 11.1713 23 11.7428 23 12.3467V18H20.5C20.5 19.6569 19.1569 21 17.5 21C15.8431 21 14.5 19.6569 14.5 18H11C11 19.6569 9.65685 21 8 21C6.34315 21 5 19.6569 5 18H4C3.44772 18 3 17.5523 3 17V16L5.76381 16.0001C6.31313 15.3863 7.11145 15 8 15C8.88855 15 9.68687 15.3863 10.2362 16.0001H15.2638C15.8131 15.3863 16.6115 15 17.5 15C18.3885 15 19.1869 15.3863 19.7362 16.0001L21 16V12L18.6123 10.41L16.6187 5H13.5239C13.1255 5 12.7651 4.76351 12.6066 4.39803L12 3H16.5675ZM17.5 16.5C16.6716 16.5 16 17.1716 16 18C16 18.8284 16.6716 19.5 17.5 19.5C18.3284 19.5 19 18.8284 19 18C19 17.1716 18.3284 16.5 17.5 16.5ZM8 16.5C7.17157 16.5 6.5 17.1716 6.5 18C6.5 18.8284 7.17157 19.5 8 19.5C8.82843 19.5 9.5 18.8284 9.5 18C9.5 17.1716 8.82843 16.5 8 16.5Z'
              fill={red}
            />
            <path
              d='M8 1C11.864 1 15 4.136 15 8C15 11.864 11.864 15 8 15C4.136 15 1 11.864 1 8C1 4.136 4.136 1 8 1ZM8 3C5.24 3 3 5.24 3 8C3 10.76 5.24 13 8 13C10.76 13 13 10.76 13 8C13 5.24 10.76 3 8 3ZM8.5 4H7V9L10.62 11.16L11.37 9.93L8.5 8.25V4Z'
              fill={red}
            />
          </svg>
        )
      case 'order_late_to_be_collected':
        return <StorefrontIcon htmlColor={red} sx={{ fontSize: size }} />
      default:
        return null
    }
  }

  return (
    <Tooltip title={getTooltip()} arrow>
      <Box height='100%' display='flex' alignItems='center'>
        {getIcon()}
      </Box>
    </Tooltip>
  )
}
